/* eslint-disable no-magic-numbers */

import { primitives as p } from './primitives';

// Semantic tokens — the only colors components should reference.
// Names and mappings mirror the design system 1:1. Primitives stay out of components.
//
// Every token points at a primitive, never at another token. Tokens sharing a hex
// are coincidental, not linked: repointing one leaves the rest untouched.
//
// Declared as consts so the component blocks further down can reference tokens
// by name instead of reaching back into the primitives.
const surface = {
	1: p.neutral[900],
	2: p.neutral[850],
	3: p.neutral[800],
	4: p.neutral[700],
};

const fill = {
	primary: p.neutral[0],
	secondary: p.neutral[100],
	tertiary: p.neutral[200],
	interactive: p.neutral[400],
	dimmed: 'rgba(245, 245, 245, 0.08)',
	dimmedSecondary: 'rgba(245, 245, 245, 0.12)',
	inverted: p.neutral[900],
	invertedSecondary: p.neutral[800],
	disabled: p.neutral[600],
	brand: p.green[500],
	brandSecondary: p.green[600],
	// Not in the design system: the strong button's pressed fill, a step darker than brandSecondary.
	brandTertiary: p.green[700],
	success: p.green[900],
	danger: p.red[800],
	dangerSecondary: p.red[700],
	dangerTertiary: p.red[300],
	warning: p.orange[800],
	caution: p.yellow[800],
	info: p.blue[800],
	infoSecondary: p.blue[500],
	infoTertiary: p.blue[300],
};

const text = {
	primary: p.neutral[0],
	// Not on the palette: the design system's text-secondary token is #C9C9C9, between neutral 100 and 200.
	secondary: '#C9C9C9',
	tertiary: p.neutral[300],
	inverted: p.neutral[900],
	invertedSecondary: p.neutral[700],
	disabled: p.neutral[400],
	brand: p.green[500],
	success: p.green[300],
	danger: p.red[400],
	dangerSecondary: p.red[300],
	warning: p.orange[200],
	caution: p.orange[200],
	// Not in the design system: dark text on the light fill.infoTertiary chip (SearchInput).
	info: p.blue[800],
};

const border = {
	interactive: p.neutral[400],
	active: p.neutral[0],
	decorative: 'rgba(245, 245, 245, 0.27)',
	inverted: p.neutral[900],
	invertedSecondary: p.neutral[800],
	disabled: p.neutral[600],
	brand: p.green[500],
	success: p.green[500],
	successSecondary: p.green[300],
	danger: p.red[400],
	dangerSecondary: p.red[200],
	caution: p.orange[500],
	cautionSecondary: p.orange[200],
	warning: p.yellow[500],
	warningSecondary: p.yellow[200],
	info: p.blue[500],
	infoSecondary: p.blue[200],
};

const motion = {
	duration: {
		// Hover tracking only — short enough to read as the pointer, not a delay.
		instant: '50ms',
		fast: '200ms',
		default: '250ms',
		slow: '500ms',
	},
	easing: {
		default: 'cubic-bezier(0.80, 0, 0.20, 1)',
		snappy: 'cubic-bezier(0.95, 0, 0.05, 1)',
	},
};

// Click-target heights. Checkbox and radio share one scale: the drawn control
// keeps its size at every setting and the target grows around it.
const controlTarget = {
	large: 48,
	compact: 36,
	min: 24,
};

// Stroke thickens on press so a control reads as engaged even where the border
// colour does not change.
const controlStroke = {
	rested: 1.5,
	pressed: 2,
};

// Scrim behind any overlay surface — modal, slide-in. Not a palette colour.
const scrim = 'rgba(0, 0, 0, 0.6)';

const shadow = {
	// Lifts a floating surface (dialog, AI chat card) off the panel behind it.
	raised: '0px 4px 16px 0px rgba(0, 0, 0, 0.36)',
	inset: '-0.5px 0.5px 1px 0 rgba(245, 245, 245, 0.16) inset',
	// A field's 1px stroke reads as 1.5px while it is engaged. The extra 0.5px is
	// an inset shadow, not a wider border — a wider border would grow the box and
	// shift the layout.
	engaged: `inset 0 0 0 0.5px ${border.active}`,
	focus: '0 0 0 2px rgba(13, 242, 13, 0.24)',
	// border.danger (red-400) at 24%.
	focusDanger: '0 0 0 2px rgba(251, 95, 81, 0.24)',
};

export const mainTheme = {
	name: 'Main',
	primaryTextColor: text.primary,

	surface,
	scrim,
	fill,
	text,
	border,
	shadow,
	motion,

	form: {
		label: {
			color: text.primary,
			// A control's label carries the same weight as its box: bright while
			// the box is bright, dimmed while it is dimmed. So an unchecked row
			// rests dimmed and lifts when engaged, and a checked row does the
			// reverse — it rests bright and dims under the pointer.
			dimmed: text.tertiary,
			requiredAsteriskColor: text.danger,
			fontWeight: 500,
			fontSize: 14,
		},
		requiredAsterisk: {
			color: text.danger,
			fontWeight: 500,
		},
		error: {
			fontWeight: 400,
			fontSize: 14,
			color: text.danger,
		},
		// Matches form.dropdown.box — an input and a dropdown trigger should be
		// indistinguishable at rest.
		input: {
			accentColor: fill.brand,
			// Transparent at rest: an input shows whatever surface it sits on, and
			// the stroke is what draws the box.
			background: 'transparent',
			border: border.decorative,
			error: {
				border: border.danger,
				shadow: shadow.focusDanger,
			},
			focus: {
				border: border.active,
				shadow: shadow.focus,
			},
			color: text.primary,
			placeholderColor: text.tertiary,
			size: {
				default: {
					height: 40,
					fontSize: 14,
					iconSize: 16,
					padding: '8px 10px',
				},
				small: {
					height: 32,
					fontSize: 14,
					iconSize: 14,
					padding: '4px 10px',
				}
			}
		},
		radio: {
			target: controlTarget,
			stroke: controlStroke,
			default: {
				border: border.interactive,
				background: 'transparent',
			},
			hover: {
				border: border.active,
			},
			// No focus glow — the white stroke carries focus on its own.
			focus: {
				border: border.active,
			},
			// Checked fills the circle and punches the dot out of it, so the dot is
			// drawn in the inverted fill rather than a brand colour. The disc keeps
			// its colour throughout — the dot is what moves, dimming when engaged and
			// growing on press so the ring thins.
			checked: {
				border: fill.primary,
				background: fill.primary,
				dot: fill.inverted,
				engagedDot: fill.invertedSecondary,
			},
		},
		checkbox: {
			// Click target only. The drawn box stays 16px at every size — the target
			// grows around it, so swapping sizes never moves the box in the layout.
			target: controlTarget,
			stroke: controlStroke,
			default: {
				border: border.interactive,
				background: 'transparent',
			},
			hover: {
				border: border.active,
			},
			// No focus glow — the white stroke carries focus on its own, and the
			// green halo read as a state change on every click.
			focus: {
				border: border.active,
			},
			// Checked and indeterminate share the light box; only the glyph differs.
			// Hover and pressed step the fill down, so the box dims as it is engaged.
			// Engaged covers both hover and press — the pressed stroke is what
			// separates them, not another fill.
			checked: {
				border: fill.primary,
				background: fill.primary,
				glyph: text.inverted,
				engaged: fill.tertiary,
				engagedGlyph: text.disabled,
			},
		},
		toggle: {
			// Row height, shared with checkbox and radio. The drawn track stays
			// 48x24 at every size — the clickable row grows around it, label
			// included, so a bigger size only makes the row easier to hit.
			target: controlTarget,
			track: {
				width: 48,
				height: 24,
				// Gap between the track edge and the knob, both at rest and thrown.
				padding: 3,
				stroke: controlStroke.rested,
			},
			// The knob stretches on press instead of the track thickening its
			// stroke, so a held toggle reads as engaged without moving the track.
			knob: {
				width: 24,
				height: 18,
				pressedWidth: 28,
			},
			// Off rests transparent and washes under the pointer; on rests bright and
			// its knob dims as it is engaged. Hover and press share one engaged look
			// — the stretched knob is what separates them.
			default: {
				background: 'transparent',
				border: border.interactive,
				knob: fill.secondary,
			},
			engaged: {
				background: fill.dimmedSecondary,
				border: border.active,
				knob: fill.primary,
			},
			// No focus glow — the white stroke carries focus, as on checkbox/radio.
			focus: {
				border: border.active,
			},
			// The track has no visible stroke when on: its border matches its fill,
			// keeping the geometry identical to the off state so nothing shifts.
			checked: {
				background: fill.primary,
				border: fill.primary,
				knob: fill.inverted,
				engagedKnob: fill.invertedSecondary,
			},
		},
		dropdown: {
			// Trigger box. Kept separate from form.input so the dropdown can move to
			// the new palette without restyling every other input.
			//
			// Closed and open are two resting states and each one dims as it is
			// engaged: a closed box brightens under the pointer, an open one falls
			// back. The chevron runs ahead of both — it goes brand green the moment
			// the box is held, whichever way the box itself is moving.
			box: {
				background: 'transparent',
				border: border.decorative,
				color: text.secondary,
				placeholder: text.tertiary,
				chevron: fill.tertiary,
				hover: {
					border: border.active,
					color: text.primary,
					chevron: fill.primary,
				},
				pressed: {
					border: border.active,
					chevron: fill.brand,
				},
				open: {
					background: surface[4],
					border: border.active,
					color: text.primary,
					chevron: fill.primary,
					// Engaged covers hover and press: an open box has nowhere brighter
					// to go, so both dim it back to the resting stroke and label.
					engagedBorder: border.interactive,
					engagedColor: text.secondary,
					engagedChevron: fill.brand,
				},
				disabled: {
					background: surface[1],
					color: text.disabled,
				},
			},
			// Menu sits two surface steps above the panel behind it and carries the
			// inset highlight, so it reads as lifted without a stroke of its own.
			// Rows have no fill at rest — the pointer washes them, and the chosen
			// row is the one whose label stays bright.
			menu: {
				default: surface[4],
				shadow: '0px 6px 12px 0px rgba(0, 0, 0, 0.36)',
				border: border.decorative,
				color: text.secondary,
				selected: text.primary,
				icon: text.secondary,
				hover: {
					background: fill.dimmed,
					color: text.primary,
				},
				pressed: {
					background: fill.dimmedSecondary,
					color: text.primary,
				},
				disabled: {
					color: text.disabled,
				},
				danger: {
					color: text.danger,
				}
			},
			restricted: {
				border: border.caution,
			},
		},
		help: {
			color: text.tertiary,
		},
		disabled: {
			opacity: 0.5,
			background: surface[1],
		},
		border: {
			width: 1,
			radius: 4,
		},

		multiSelectInput: {
			itemBackground: surface[4],
			removeIcon: text.tertiary,
			removeIconHover: text.primary,
			itemColor: text.primary,
		},

	},
	stepper: {
		stepIndicator: {
			// Current and completed steps share the white circle — the indicator's
			// content (number vs checkmark) is what separates them.
			primary: {
				background: fill.primary,
				color: text.inverted,
			},
			neutral: {
				background: surface[4],
				color: text.tertiary,
			}
		},
		stepLabel: {
			primary: {
				color: text.primary,
			},
			neutral: {
				color: text.tertiary,
			},
		},
		stepButton: {
			color: text.brand,
		},
		connector: border.decorative,
	},
	slideIn: {
		overlay: scrim,
		title: {
			color: text.primary,
			fontWeight: 500,
			fontSize: 18,
		},
		background: surface[2],
		closeIcon: {
			color: text.tertiary,
			hover: text.primary,
		},
		border: border.decorative,
	},
	card: {
		title: text.primary,
		subtitle: text.tertiary,
		color: text.secondary,
		background: surface[2],
		default: {
			border: border.decorative,
		},
		selected: {
			border: border.success,
			boxShadow: shadow.focus,
		}
	},
	tabs: {
		error: {
			color: text.danger,
			icon: text.danger,
		},
		// Underline under the active tab; hovering an inactive tab previews it in white.
		highlight: border.success,
		hoverHighlight: border.active,
		default: {
			color: text.tertiary,
			icon: text.tertiary,
		},
		hover: {
			color: text.primary,
			icon: text.primary,
		},
		active: {
			color: text.primary,
			icon: text.primary,
		},
	},
	// Progress fills need the vivid 500 steps. fill.success/danger/warning are the dark
	// 800s, meant as surfaces behind light text, so the border tokens are the right source.
	progress: {
		track: fill.dimmed,
		success: border.success,
		warning: border.warning,
		danger: border.danger,
	},
	initials: {
		background: fill.dimmed,
		color: text.primary,
	},
	emptyState: {
		background: surface[2],
		text: {
			color: text.primary,
		},
		icon: {
			background: surface[4],
			color: fill.tertiary,
		},
	},
	font: '"Geist", -apple-system, BlinkMacSystemFont, sans-serif',
	upgradePages: {
		boxShadow: '0px 6px 12px 0px rgba(0, 0, 0, 0.36)',
		focus: {
			shadow: shadow.focus,
		}
	},
	featureText: {
		color: text.tertiary
	},
	slider: {
		track: {
			background: fill.dimmed,
		},
		thumb: {
			background: fill.brand,
			border: border.success,
			fill: text.inverted,
		},
	},
	widgetStatus: {
		neutral: {
			color: text.tertiary,
		},
		warning: {
			color: text.tertiary,
			iconColor: text.warning,
		},
		error: {
			color: text.danger,
			background: fill.danger,
			iconColor: text.danger,
		},
	},
	aiChat: {
		// The chat floats over the page, so it sits a surface above it (surface[2] blended in),
		// and its messages a step above that.
		card: {
			background: surface[3],
			border: border.decorative,
			shadow: shadow.raised,
		},
		header: {
			title: text.primary,
			border: border.decorative,
			background: surface[3],
		},
		messageList: {
			background: surface[3],
		},
		message: {
			user: {
				background: fill.dimmedSecondary,
				color: text.primary,
			},
			assistant: {
				background: surface[4],
				color: text.primary,
			},
			error: {
				background: fill.danger,
				color: text.danger,
				border: border.dangerSecondary,
			},
		},
		input: {
			background: surface[1],
			border: border.interactive,
			focusBorder: border.active,
			focusShadow: shadow.focus,
			placeholder: text.tertiary,
			color: text.primary,
		},
		pill: {
			background: surface[3],
			backgroundHover: surface[4],
			color: text.primary,
			expandedBorder: border.interactive,
			expandedHalo: shadow.focus,
			expandedBackground: surface[2],
			leadingIcon: text.brand,
			placeholder: text.tertiary,
			sendBackground: fill.brand,
			sendBackgroundHover: text.success,
			sendColor: text.inverted,
		},
	},
	coinsLabel: {
		background: fill.caution,
		text: border.warning,
	},
	// Buttons carry their own disabled pair, dimmer than text/disabled and fill/disabled.
	// Off the token set on purpose: the design specs these for buttons only.
	button: {
		disabled: {
			color: p.neutral[500],
			background: 'rgba(240, 240, 240, 0.04)',
		},
	},

};

/**
 * Builds a transition using the default motion tokens.
 * Usage: transition: ${transition('background', 'border')};
 */
export const transition = (...properties: string[]) =>
	({ theme }: { theme: typeof mainTheme }) =>
		properties
			.map(prop => `${prop} ${theme.motion.duration.default} ${theme.motion.easing.default}`)
			.join(', ');
